// src/pages/TestCategoryTable.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEdit3, FiTrash2 } from "react-icons/fi";
import { FaChartBar } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

export default function TestCategoryTable() {
  const { id: testId } = useParams();
  const [categories, setCategories] = useState([]);
  const [testName, setTestName] = useState("");
const [linkedCounts, setLinkedCounts] = useState({});
  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (key, def) => translations[key] || def;

  const token = localStorage.getItem("admin_token");
  const base  = process.env.REACT_APP_API_BASE_URL;

  /* ---- Test adı getir ---- */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${base}/api/tests/${testId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const name =
          data?.name ??
          data?.title ??
          data?.test_name ??
          "";
        setTestName(name || `Test #${testId}`);
      } catch {
        setTestName(`Test #${testId}`);
      }
    })();
  }, [testId]);

  /* ---- Bu testin kategorilerini getir ---- */
  const fetchCategories = async () => {
    try {
      const resp = await axios.get(
        `${base}/api/test-categories/${testId}/categories`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = Array.isArray(resp.data)
        ? resp.data
        : Array.isArray(resp.data?.data)
        ? resp.data.data
        : Array.isArray(resp.data?.rows)
        ? resp.data.rows
        : [];
      setCategories(list);
    } catch (err) {
      console.error("Kategori verileri alınamadı:", err);
      toast.error(t("CATEGORY_LIST_ERROR", "Kategori listesi alınamadı!"));
    }
  };
// ⬇️ BU TESTTEKİ SORULARI ÇEKİP KATEGORİYE BAĞLI SAYILARI HESAPLA
  const fetchLinkedCounts = async () => {
    try {
      const { data } = await axios.get(`${base}/api/tests/${testId}/questions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const counts = {};
      (Array.isArray(data) ? data : []).forEach((q) => {
        // Soru -> kategoriler (çeşitli alan adlarını destekleyelim)
        const ids = new Set();
        if (Array.isArray(q?.TestCategories)) q.TestCategories.forEach(c => ids.add(c?.id ?? c?.category_id));
        if (Array.isArray(q?.categories))     q.categories.forEach(c => ids.add(typeof c === "object" ? (c.id ?? c.category_id) : c));
        if (q?.category_id != null)           ids.add(q.category_id);
        if (q?.primary_category_id != null)   ids.add(q.primary_category_id);

        ids.forEach(id => {
          if (id != null) counts[id] = (counts[id] || 0) + 1;
        });
      });
      setLinkedCounts(counts);
    } catch (e) {
      console.error("Bağlı soru sayıları alınamadı:", e);
      setLinkedCounts({});
    }
  };


  useEffect(() => {
    fetchCategories();
        fetchLinkedCounts();
  }, [testId]);

  const handleDelete = (catId) => {
    // Güvenlik: bağlı ise hiç açma
    if ((linkedCounts[catId] || 0) > 0) return;

    confirmAlert({
      title: t("CATEGORY_DELETE_CONFIRM_TITLE", "Kategoriyi Sil"),
      message: t("CATEGORY_DELETE_CONFIRM_MSG", "Bu kategoriyi silmek istediğinize emin misiniz?"),
      buttons: [
        {
          label: t("YES_DELETE", "Evet, Sil"),
          onClick: async () => {
            try {
              await axios.delete(`${base}/api/tests/${testId}/categories/${catId}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              toast.success(t("CATEGORY_DELETED", "Kategori silindi!"));
              await Promise.all([fetchCategories(), fetchLinkedCounts()]);
            } catch (err) {
              console.error("Silme hatası:", err);
              toast.error(err.response?.data?.message || t("CATEGORY_DELETE_ERROR", "Kategori silinemedi!"));
            }
          },
        },
        { label: t("CANCEL", "İptal"), onClick: () => {} },
      ],
    });
  };

  /* ---- Sütunlar ---- */
  const columns = useMemo(() => [
    { accessorKey: "id", header: t("TABLE_ID_LABEL", "ID"), size: 60 },

    {
      accessorKey: "image_url",
      header: t("IMAGE", "Resim"),
      size : 80,
      cell : info => {
        const url = info.getValue();
        if (!url) return "-";
        const src = String(url).startsWith("http")
          ? url
          : `${base}${url}`;
        return (
          <img
            src={src}
            alt="Kategori"
            style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
          />
        );
      }
    },

    { accessorKey: "name", header: t("CATEGORY_NAME", "Kategori Adı") },

    {
      accessorKey: "created_at",
      header: t("CREATED_AT", "Oluşturma Tarihi"),
      size : 130,
      cell : info => (
        <div style={{ textAlign: "end", width: 125 }}>
          {info.getValue() ? new Date(info.getValue()).toLocaleDateString("tr-TR") : "-"}
        </div>
      )
    },

    /* ——— A C T I O N S ——— */
    {
      accessorKey   : "actions",
      header        : t("TABLE_ACTIONS_LABEL", "İşlemler"),
      enableSorting : false,
      size          : 150,
      cell: info => {
        const cat    = info.row.original;
        const catId  = cat.id ?? cat.category_id; // önce id, yoksa category_id
        const testIdRef = cat.test_id || testId;
                const count = linkedCounts[catId] || 0;
        const disabled = count > 0;

        return (
          <div className="hstack gap-2 justify-content-end">
            {/* Puan açıklamaları */}
            <Link
              to={`/test/${testIdRef}/category/${catId}/score/table`}
              className="avatar-text avatar-md text-info bg-soft-info border-soft-info"
              title={t("SCORE_EXPLAINS", "Puan Açıklamaları")}
            >
              <FaChartBar />
            </Link>

            {/* Düzenle */}
            <Link
              to={`/test/${testIdRef}/category/edit/${catId}`}
              className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
              title={t("EDIT_CATEGORY", "Düzenle")}
            >
              <FiEdit3 />
            </Link>

 {/* Sil — devre dışı ama tıklanınca hiçbir şey yapmıyor; tooltip var, yasak imleci yok */}
            <span
              role="button"
              aria-disabled={disabled}
              title={
                disabled
                  ? t("CATEGORY_LINKED_WARNING", "Bu kategoriye bağlı sorular var, silemezsiniz")
                  : t("DELETE_CATEGORY", "Sil")
              }
              onClick={() => { if (!disabled) handleDelete(catId); }}
              onMouseDown={(e) => { if (disabled) e.preventDefault(); }}
              className="avatar-text avatar-md"
              style={{
                color: disabled ? "#9aa0a6" : "#dc3545",
                backgroundColor: disabled ? "#f1f3f4" : "rgba(220,53,69,0.12)",
                borderColor: disabled ? "#f1f3f4" : "rgba(220,53,69,0.12)",
                cursor: disabled ? "default" : "pointer",
                opacity: disabled ? 0.75 : 1
              }}
            >
              <FiTrash2 />
            </span>
          </div>
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [testId, t]);

  return (
    <div className="table-responsive">
      <ToastContainer />

      {/* Üst bağlam satırı */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="small text-muted">
          {t("TEST_CONTEXT", "Test")}: <strong>{testName || `Test #${testId}`}</strong>
        </div>
      
      </div>

      <Table data={categories} columns={columns} />
    </div>
  );
}
